import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const coreCategories = [
  { icon: 'ri-shield-line', label: 'Liability Coverage', desc: 'Protects against lawsuits, bodily injury, and property damage claims.' },
  { icon: 'ri-building-line', label: 'Property Coverage', desc: 'Covers your building, equipment, and inventory from fire, theft, or storms.' },
  { icon: 'ri-truck-line', label: 'Commercial Auto', desc: 'Required if your business uses any vehicles — from work trucks to full fleets.' },
  { icon: 'ri-first-aid-kit-line', label: 'Workers Compensation', desc: 'Covers employee injuries, medical bills, and lost wages. Often required by law.' },
];

const coreCoverages = [
  {
    icon: 'ri-shield-check-line',
    title: 'General Liability (GL)',
    tag: 'Most Common',
    body: 'General liability insurance protects your business from lawsuits involving bodily injury or property damage. If a customer slips and falls at your location, or you accidentally damage someone else\'s property on a job, GL is what responds. It\'s the foundation of any commercial insurance program.',
    example: 'A customer slips and falls at your location or you accidentally damage someone else\'s property on a job.',
  },
  {
    icon: 'ri-building-2-line',
    title: 'Commercial Property',
    tag: 'Essential',
    body: 'This covers your building, equipment, and inventory against risks like fire, theft, or storms. It\'s important to insure based on Total Insured Value (TIV) — the full replacement value of everything you need to protect. Underinsuring your property is one of the most common and costly mistakes business owners make.',
    example: 'A fire destroys your equipment and inventory. Without proper TIV coverage, you may only recover a fraction of your losses.',
  },
  {
    icon: 'ri-truck-line',
    title: 'Commercial Auto',
    tag: 'Required for Fleets',
    body: 'If your business uses vehicles, this coverage is essential. It applies to work trucks, fleets, coal hauling trucks, dump trucks, and tow trucks. Auto liability claims can be extremely expensive — especially in high-risk industries like trucking and mining where vehicles are large and accidents are severe.',
    example: 'A company truck is involved in an accident on a job site. Personal auto insurance will not cover commercial use.',
  },
  {
    icon: 'ri-first-aid-kit-line',
    title: 'Workers Compensation',
    tag: 'Often Required by Law',
    body: 'Workers comp covers employee injuries, medical bills, and lost wages. In industries like mining, logging, and construction, this is one of the most important coverages you can carry — and it\'s often required by law. Requirements vary by state, so it\'s critical to understand the rules in every state where you operate.',
    example: 'An employee is injured on a job site. Workers comp covers their medical bills and a portion of lost wages while they recover.',
  },
];

const industryRisks = [
  { icon: 'ri-hammer-line', industry: 'Coal Mining', risks: ['Equipment loss and breakdown', 'Black lung and occupational disease exposure', 'High liability from mining operations', 'Strict federal and state regulatory requirements'] },
  { icon: 'ri-truck-line', industry: 'Trucking', risks: ['Large auto liability claims', 'Cargo loss and damage', 'Strict DOT and FMCSA regulations', 'Driver injury and workers comp exposure'] },
  { icon: 'ri-tools-line', industry: 'Contractors', risks: ['Job site injuries and workers comp', 'Subcontractor liability exposure', 'Property damage on client sites', 'Completed operations claims after the job'] },
  { icon: 'ri-tree-line', industry: 'Logging', risks: ['Dangerous equipment and machinery', 'Remote locations with limited emergency access', 'High workers comp rates', 'Equipment breakdown and property loss'] },
];

const mistakes = [
  { icon: 'ri-money-dollar-circle-line', title: 'Being Underinsured to Save Money', body: 'Cutting coverage to lower your premium is one of the most dangerous decisions a business owner can make. When a major claim hits, the savings disappear instantly — and you\'re left covering the gap out of pocket.' },
  { icon: 'ri-file-warning-line', title: 'Using Incorrect Classifications', body: 'Insurance premiums are calculated based on how your business is classified. Using the wrong classification — intentionally or not — can result in denied claims, policy cancellations, or significant premium adjustments at audit.' },
  { icon: 'ri-car-line', title: 'Not Listing All Equipment or Vehicles', body: 'Every piece of equipment and every vehicle needs to be listed on your policy. If a vehicle or piece of equipment isn\'t scheduled, it may not be covered when you need it most.' },
  { icon: 'ri-umbrella-line', title: 'Skipping Key Coverages', body: 'Many businesses skip umbrella policies or pollution coverage to save money. These coverages exist for catastrophic events — the exact situations where you need the most protection.' },
  { icon: 'ri-file-list-3-line', title: 'No Subcontractor Agreements', body: 'Working with subcontractors without proper agreements and certificates of insurance can expose your business to significant liability. Always require proof of coverage from every sub before work begins.' },
];

const choosingSteps = [
  { num: '01', title: 'Work With an Industry Specialist', body: 'Not all agents understand high-risk industries. Work with someone who knows your business, your state\'s requirements, and the carriers that specialize in your type of work.' },
  { num: '02', title: 'Compare Multiple Carriers', body: 'The right policy comes from comparing both standard and high-risk (E&S) markets. A specialist agent has access to markets that a general agent may not, which means better coverage at better rates.' },
  { num: '03', title: 'Make Sure Your Limits Are High Enough', body: 'Minimum limits are rarely enough. Work with your agent to determine the right limits based on your revenue, assets, and the size of claims common in your industry.' },
  { num: '04', title: 'Review Exclusions Carefully', body: 'Every policy has exclusions — things it won\'t cover. Understanding what\'s excluded is just as important as understanding what\'s covered. Ask your agent to walk through the exclusions with you.' },
  { num: '05', title: 'Update Your Policy as You Grow', body: 'Your business changes. New equipment, new employees, new locations — all of these affect your coverage needs. Review your policy at least annually and after any major business change.' },
];

export default function BuyersGuideArticle() {
  useEffect(() => {
    document.title = 'Business Insurance Buyer\'s Guide KY TN VA WV | Summit Appalachia';

    // Meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) { metaDesc = document.createElement('meta'); metaDesc.setAttribute('name', 'description'); document.head.appendChild(metaDesc); }
    metaDesc.setAttribute('content', 'Everything Appalachian business owners in KY, TN, VA, and WV need to know before purchasing commercial coverage — from core policies to industry-specific risks and common mistakes.');

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.setAttribute('rel', 'canonical'); document.head.appendChild(canonical); }
    canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL}/resources/business-insurance-buyers-guide`);

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      '@id': `${import.meta.env.VITE_SITE_URL}/resources/business-insurance-buyers-guide#article`,
      url: `${import.meta.env.VITE_SITE_URL}/resources/business-insurance-buyers-guide`,
      headline: 'Business Insurance Buyer\'s Guide for Appalachian Business Owners',
      description: 'Everything Appalachian business owners in KY, TN, VA, and WV need to know before purchasing commercial coverage — from core policies to industry-specific risks.',
      datePublished: '2026-03-01',
      dateModified: '2026-04-06',
      author: { '@type': 'Organization', name: 'Summit Appalachia Insurance' },
      publisher: {
        '@type': 'Organization',
        name: 'Summit Appalachia Insurance',
        url: import.meta.env.VITE_SITE_URL,
      },
      isPartOf: { '@id': `${import.meta.env.VITE_SITE_URL}/#website` },
      about: [
        { '@type': 'Thing', name: 'Business Insurance' },
        { '@type': 'Thing', name: 'Commercial Insurance' },
        { '@type': 'Thing', name: 'Appalachian Industries' },
      ],
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-buyers-guide';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => {
      document.getElementById('schema-buyers-guide')?.remove();
      // Restore homepage canonical
      const can = document.querySelector('link[rel="canonical"]');
      if (can) can.setAttribute('href', import.meta.env.VITE_SITE_URL ?? '/');
    };
  }, []);

  return (
    <div className="font-sans bg-[#000000]">
      <Navbar />

      {/* Hero */}
      <section className="relative h-[420px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/be67fa809711a71cae86afb361e344e7.jpg"
            alt="Business Insurance Buyer's Guide"
            className="w-full h-full object-cover object-top opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-10 pb-16 w-full">
          <p className="text-white/30 text-xs font-sans mb-3 uppercase tracking-widest">
            <Link to="/" className="hover:text-[#D4AF37] transition-colors">Home</Link>
            {' '}/ <Link to="/resources" className="hover:text-[#D4AF37] transition-colors">Resources</Link>
            {' '}/ Guide
          </p>
          <div className="flex items-center gap-3 mb-4">
            <span className="bg-[#D4AF37] text-black text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-sm">Guides</span>
            <span className="text-white/30 text-xs font-sans">8 min read · Mar 2026</span>
          </div>
          <h1 className="font-serif text-4xl md:text-6xl text-white leading-tight">
            Business Insurance<br />
            <span className="text-[#D4AF37]">Buyer's Guide</span>
          </h1>
          <p className="text-white/50 text-base font-sans mt-4 max-w-2xl leading-relaxed">
            Everything Appalachian business owners need to know before purchasing commercial coverage — from core policies to industry-specific risks.
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-14 bg-[#000000]">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="border-l-2 border-[#D4AF37] pl-6 mb-12">
            <p className="text-white/70 text-base font-sans leading-relaxed">
              If you own a business in Kentucky, Tennessee, Virginia, or West Virginia, having the right insurance isn't optional — it's critical. From coal mining and trucking to contractors and small local businesses, Appalachian industries come with unique risks that standard policies don't always cover. The truth is, <strong className="text-white">most businesses are either underinsured or overpaying.</strong> This guide will help you understand what coverage you actually need, what to avoid, and how to protect your business the right way.
            </p>
          </div>

          {/* Core Categories Overview */}
          <h2 className="font-serif text-2xl text-white mb-6">The Four Core Coverage Categories</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-16">
            {coreCategories.map((cat, i) => (
              <div key={i} className="flex items-start gap-4 bg-[#111111] border border-[#D4AF37]/15 rounded-md p-5">
                <div className="w-9 h-9 flex items-center justify-center text-[#D4AF37] flex-shrink-0">
                  <i className={`${cat.icon} text-xl`} />
                </div>
                <div>
                  <span className="text-white text-sm font-bold font-sans">{cat.label}</span>
                  <p className="text-white/40 text-xs font-sans leading-relaxed mt-1">{cat.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Coverages Deep Dive */}
      <section className="py-16 bg-[#0a0a0a]">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <h2 className="font-serif text-3xl text-white mb-2">Core Coverages Explained</h2>
          <p className="text-white/40 text-sm font-sans mb-10 leading-relaxed">Understanding these basics is the first step to building the right policy.</p>
          <div className="space-y-6">
            {coreCoverages.map((cov, i) => (
              <div key={i} className="bg-[#111111] border border-[#D4AF37]/15 rounded-md p-7 hover:border-[#D4AF37]/35 transition-colors">
                <div className="flex flex-col sm:flex-row sm:items-start gap-5">
                  <div className="w-10 h-10 flex items-center justify-center text-[#D4AF37] flex-shrink-0">
                    <i className={`${cov.icon} text-2xl`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3 mb-3">
                      <h3 className="font-serif text-xl text-white">{cov.title}</h3>
                      <span className="text-[#D4AF37] text-xs font-bold uppercase tracking-widest font-sans border border-[#D4AF37]/40 px-2 py-0.5 rounded-sm">{cov.tag}</span>
                    </div>
                    <p className="text-white/55 text-sm font-sans leading-relaxed mb-4">{cov.body}</p>
                    <div className="bg-[#0a0a0a] border-l-2 border-[#D4AF37]/50 pl-4 py-2">
                      <span className="text-[#D4AF37] text-xs font-bold uppercase tracking-widest font-sans">Example: </span>
                      <span className="text-white/40 text-xs font-sans leading-relaxed">{cov.example}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industry-Specific Risks */}
      <section className="py-16 bg-[#000000]">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <h2 className="font-serif text-3xl text-white mb-2">Industry-Specific Risks in Appalachia</h2>
          <p className="text-white/40 text-sm font-sans mb-10 leading-relaxed">
            Not all businesses are the same. Appalachian industries face higher risks than standard businesses — and require specialized insurance, not a one-size-fits-all policy.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {industryRisks.map((ind, i) => (
              <div key={i} className="bg-[#111111] border border-[#D4AF37]/15 rounded-md p-6 hover:border-[#D4AF37]/35 transition-colors">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 flex items-center justify-center text-[#D4AF37]">
                    <i className={`${ind.icon} text-xl`} />
                  </div>
                  <h3 className="font-sans text-sm font-bold text-white uppercase tracking-wide">{ind.industry}</h3>
                </div>
                <ul className="space-y-2">
                  {ind.risks.map((risk, ri) => (
                    <li key={ri} className="flex items-start gap-2 text-white/45 text-xs font-sans leading-relaxed">
                      <span className="text-[#D4AF37] mt-0.5 flex-shrink-0">—</span>
                      {risk}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Common Mistakes */}
      <section className="py-16 bg-[#0a0a0a]">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <h2 className="font-serif text-3xl text-white mb-2">Common Mistakes to Avoid</h2>
          <p className="text-white/40 text-sm font-sans mb-10 leading-relaxed">
            These mistakes can cost you hundreds of thousands — or more — during a claim.
          </p>
          <div className="space-y-4">
            {mistakes.map((m, i) => (
              <div key={i} className="flex gap-5 bg-[#111111] border border-[#D4AF37]/15 rounded-md p-5 hover:border-[#D4AF37]/35 transition-colors">
                <div className="w-9 h-9 flex items-center justify-center text-[#D4AF37] flex-shrink-0 mt-0.5">
                  <i className={`${m.icon} text-lg`} />
                </div>
                <div>
                  <h3 className="font-sans text-sm font-bold text-white mb-1">{m.title}</h3>
                  <p className="text-white/45 text-sm font-sans leading-relaxed">{m.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Choose */}
      <section className="py-16 bg-[#000000]">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <h2 className="font-serif text-3xl text-white mb-2">How to Choose the Right Policy</h2>
          <p className="text-white/40 text-sm font-sans mb-10 leading-relaxed">
            The cheapest policy is almost never the best policy. Here's how to shop smart.
          </p>
          <div className="space-y-5">
            {choosingSteps.map((step, i) => (
              <div key={i} className="flex gap-6 items-start">
                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center border border-[#D4AF37]/30 rounded-md">
                  <span className="text-[#D4AF37] font-serif text-sm font-bold">{step.num}</span>
                </div>
                <div className="flex-1 border-b border-white/5 pb-5">
                  <h3 className="font-sans text-sm font-bold text-white mb-1">{step.title}</h3>
                  <p className="text-white/45 text-sm font-sans leading-relaxed">{step.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Summit Appalachia */}
      <section className="py-16 bg-[#0a0a0a]">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="bg-[#111111] border border-[#D4AF37]/30 rounded-md p-8 md:p-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 flex items-center justify-center text-[#D4AF37]">
                <i className="ri-award-line text-xl" />
              </div>
              <h2 className="font-serif text-2xl text-white">Why Work With a Specialized Agency</h2>
            </div>
            <p className="text-white/50 text-sm font-sans leading-relaxed mb-8">
              High-risk businesses need more than a basic quote — they need strategy. At Summit Appalachia Insurance, we specialize in the industries and risks that other agencies avoid.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {[
                { icon: 'ri-hammer-line', label: 'Coal Trucking & Mining Risks' },
                { icon: 'ri-tools-line', label: 'Contractors & Commercial Auto' },
                { icon: 'ri-alert-line', label: 'Hard-to-Place & High-Risk Businesses' },
                { icon: 'ri-global-line', label: 'Standard & E&S Market Access' },
                { icon: 'ri-map-pin-2-line', label: 'All 50 States Served' },
                { icon: 'ri-group-line', label: '50+ Direct Carrier Partners' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-7 h-7 flex items-center justify-center text-[#D4AF37]">
                    <i className={`${item.icon} text-base`} />
                  </div>
                  <span className="text-white/60 text-sm font-sans">{item.label}</span>
                </div>
              ))}
            </div>
            <p className="text-white/35 text-xs font-sans leading-relaxed border-t border-white/5 pt-6">
              We help you get properly covered without overpaying — with access to both standard markets and Excess &amp; Surplus (E&amp;S) carriers for the risks others won't touch.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#000000] border-t border-[#D4AF37]/10">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">Ready for a Free Policy Review?</h2>
          <p className="text-white/40 text-sm font-sans leading-relaxed mb-8 max-w-xl mx-auto">
            If your premium went up, your policy is outdated, or you're not sure you're covered correctly — it's time for a review. Contact Summit Appalachia Insurance today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="px-8 py-3.5 bg-[#D4AF37] text-black text-xs font-bold uppercase tracking-widest rounded-md hover:bg-[#e8c84e] transition-colors whitespace-nowrap cursor-pointer"
            >
              Get a Free Quote
            </Link>
            <Link
              to="/resources"
              className="px-8 py-3.5 border border-[#D4AF37]/40 text-[#D4AF37] text-xs font-bold uppercase tracking-widest rounded-md hover:border-[#D4AF37] transition-colors whitespace-nowrap cursor-pointer"
            >
              Back to Resources
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
